import React, { useEffect } from "react";
import styled from "styled-components";
import { theme } from "../theme";
import Button from "./Button";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.surface};
  padding: 24px;
  border-radius: ${theme.radius.lg}px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.lg};
`;

const Title = styled.h2`
  margin: 0 0 20px;
  font-size: 20px;
  color: ${theme.colors.text};
  font-weight: 600;
  font-family: ${theme.fonts.body};
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: ${theme.colors.text};
  font-weight: 500;
  font-family: ${theme.fonts.body};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  font-size: 14px;
  box-sizing: border-box;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
  background: ${theme.colors.surface};

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
  background: ${theme.colors.surface};

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  font-size: 14px;
  box-sizing: border-box;
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.danger};
  font-size: 12px;
  margin: 5px 0 0;
`;

const AdminForm = ({
  isOpen,
  onClose,
  onSave,
  fields,
  initialValues,
  isLoading,
}) => {
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});

  useEffect(() => {
    if (isOpen && initialValues) {
      setValues(initialValues);
      setErrors({});
    }
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach((field) => {
      if (field.required && !values[field.name]?.trim()) {
        newErrors[field.name] = "Это поле обязательно";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const cleanedValues = {};
      Object.keys(values).forEach((key) => {
        const val = values[key];
        cleanedValues[key] = val === "" ? null : val;
      });
      onSave(cleanedValues);
    }
  };

  const renderField = (field) => {
    const value = values[field.name] ?? "";

    if (field.type === "textarea") {
      return (
        <Textarea
          name={field.name}
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.type === "select" && field.options) {
      return (
        <Select name={field.name} value={value} onChange={handleChange}>
          <option value="">Выберите...</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      );
    }

    return (
      <FormInput
        type={field.type || "text"}
        name={field.name}
        value={value}
        onChange={handleChange}
        placeholder={field.placeholder}
      />
    );
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>{initialValues ? "Редактировать" : "Добавить"}</Title>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <FormGroup key={field.name}>
              <Label>{field.label}</Label>
              {renderField(field)}
              {errors[field.name] && (
                <ErrorMessage>{errors[field.name]}</ErrorMessage>
              )}
            </FormGroup>
          ))}
          <ButtonGroup>
            <Button type="button" $variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" $variant="success" disabled={isLoading}>
              {isLoading ? "Создаём..." : "Создать"}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Overlay>
  );
};

export default AdminForm;
